import {
  ICompletedSupportForm,
  ISupplierUserProfile,
  ISupportMessage,
  IUser,
} from 'constants/interfaces';
import {Grid} from '@material-ui/core';
import React, {useCallback, useEffect, useState} from 'react';
import {
  apiGetSupportForms,
  apiGetSupportMessages,
  apiGetUser,
  apiSendSupportMessage,
  apiUploadFile,
} from 'constants/Api';
import {CustomerChat} from 'screens/Dashboard/Customers/CustomerDetail/CustomerChat';
import {useSocketEvents} from 'appsockets/SocketEventProvider';
import {PermissionsService} from 'services/PermissionsService';
import {ESupplierUserPermissions} from 'constants/index';
import {Breakpoint} from '@material-ui/core/styles/createBreakpoints';

export function CustomerDetail(props: {
  user: ISupplierUserProfile;
  customerUserId: string;
  width: Breakpoint;
}) {
  const {user, customerUserId} = props;

  const [tabIndex] = useState(0);
  const showTabs = props.width === 'xs' || props.width === 'sm';

  const [, setIsLoadingCustomer] = useState<boolean>(false);
  const [customer, setCustomer] = useState<IUser>();
  const [isLoadingMessages, setIsLoadingMessages] = useState<boolean>(false);
  // eslint-disable-next-line no-unused-vars
  const [isRefreshingMessages, setIsRefreshingMessages] =
    useState<boolean>(false);
  const [messages, setMessages] = useState<ISupportMessage[]>([]);
  const [, setIsLoadingForms] = useState<boolean>(false);
  const [, setForms] = useState<ICompletedSupportForm[]>([]);
  const {newCustomerMessage} = useSocketEvents();

  // const customerFullName = customer?.farmer
  //   ? customer.farmer.name + ' ' + customer.farmer.surname
  //   : customer
  //   ? customer.name + ' ' + customer.surname
  //   : '';

  const loadCustomer = useCallback((customerUserId: string) => {
    setIsLoadingCustomer(true);
    apiGetUser(customerUserId)
      .then(setCustomer)
      .catch((error) => {
        alert('Error loading customer. Please try again later');
        console.error(error);
      })
      .then(() => setIsLoadingCustomer(false));
  }, []);

  useEffect(() => {
    loadCustomer(customerUserId);
  }, [customerUserId, loadCustomer]);

  const loadMessages = useCallback(
    (customerUserId: string) => {
      setIsLoadingMessages(true);
      getMessages();
    },
    [user],
  );

  const refreshMessages = (customerUserId: string) => {
    setIsRefreshingMessages(true);
    getMessages();
  };

  const getMessages = () => {
    apiGetSupportMessages(user.userId, customerUserId)
      .then(setMessages)
      .catch((error) => {
        alert('Error loading messages. Please try again later');
        console.error(error);
      })
      .then(() => {
        setIsLoadingMessages(false);
        setIsRefreshingMessages(false);
      });
  };

  const handleTextMessageSend = (customerUserId: string, text: string) => {
    apiSendSupportMessage(customerUserId, text, 'TEXT')
      .then((response) => {
        if (response.success) refreshMessages(customerUserId);
        else throw new Error(response.message);
      })
      .catch((error) => {
        alert('Error sending message');
        console.error(error);
      });
  };

  const handleImageMessageSend = (customerUserId: string, image: File) => {
    const fileExtension = image.name.split('.').pop()?.substr(0, 4);
    const fileKey = `${new Date().getTime()}_${
      user.userId
    }_customer_chat.${fileExtension}`;
    setIsRefreshingMessages(true);
    apiUploadFile(image, fileKey)
      .then((imageUrl) =>
        apiSendSupportMessage(customerUserId, imageUrl, 'IMAGE'),
      )
      .then((response) => {
        if (response.success) refreshMessages(customerUserId);
        else throw new Error(response.message);
      })
      .catch((error) => {
        alert('Error sending message');
        console.error(error);
      })
      .finally(() => setIsRefreshingMessages(false));
  };

  // load on mount
  useEffect(() => {
    loadMessages(customerUserId);
  }, [customerUserId, loadMessages]);

  const loadForms = useCallback((farmerId: string) => {
    setIsLoadingForms(true);
    apiGetSupportForms(farmerId)
      .then(setForms)
      .catch((error) => {
        alert('Error loading forms. Please try again later');
        console.error(error);
      })
      .then(() => setIsLoadingForms(false));
  }, []);

  useEffect(() => {
    if (!customer || !customer.farmer?.id) return;
    loadForms(customer.farmer.id.toString());
  }, [customer, loadForms]);

  useEffect(() => {
    if (newCustomerMessage?.sender.id == customerUserId) {
      refreshMessages(customerUserId);
    }
  }, [newCustomerMessage]);

  const permissions = new PermissionsService(props.user);
  const canReadChat = permissions.hasPermission(
    ESupplierUserPermissions.CUSTOMER_CHAT_READ,
  );

  return (
    <Grid>
      <Grid
        xs={12}
        md={12}
        style={{display: showTabs && tabIndex === 0 ? 'none' : ''}}
      >
        {canReadChat && (
          <CustomerChat
            isLoading={isLoadingMessages}
            isRefreshing={isRefreshingMessages}
            user={user}
            customer={customer}
            messages={messages}
            onMessageSend={(text) =>
              handleTextMessageSend(customerUserId, text)
            }
            onImageSend={(image) =>
              handleImageMessageSend(customerUserId, image)
            }
          />
        )}
      </Grid>
    </Grid>
  );
}
